import * as React from 'react';
import useSWR from 'swr';

import { SurveyConfig, SurveyResultsResponse } from '../types';
import useIsVisible from '../hooks/useIsVisible';

import { SurveyStartTeaserProps } from '../components/SurveyStartTeaser/SurveyStartTeaser';
import SurveyResult from '../components/SurveyResult';
import SurveyStartSlidePlaceholder from '../components/SurveyStartSlidePlaceholder';
import SurveyStartTeaser from '../components/SurveyStartTeaser';
import SurveyStartTeaserSlide from '../components/SurveyStartTeaserSlide';

const fetcher = (url: string) =>
  fetch(url, {
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    }
  }).then((res) => res.json());

export type StartTeaserProps = {
  formatSlideHeadline: (question: string) => React.ReactNode;
  startTeaser: SurveyConfig;
  surveyUid: string;
} & Omit<SurveyStartTeaserProps, 'children'>;

const StartTeaser = ({
  formatSlideHeadline,
  startTeaser,
  surveyUid,
  ...props
}: StartTeaserProps) => {
  const spyRef = React.useRef<HTMLDivElement>(null);

  /**
   * Whether the start teaser is currently visible (not below the fold)
   */
  const isVisible = useIsVisible(spyRef);

  /**
   * Whether the start teaser was at least once visible (not below the fold)
   */
  const [enabled, setEnabled] = React.useState(false);

  React.useEffect(() => {
    if (isVisible && !enabled) {
      setEnabled(true);
    }
  }, [isVisible, enabled, setEnabled]);

  const { data } = useSWR<SurveyResultsResponse>(
    enabled
      ? `/api/surveys/${surveyUid}/teaser/${startTeaser.uid}/results`
      : null,
    fetcher
  );

  const questionConfigs = React.useMemo(
    () => Object.fromEntries(startTeaser.questions.map((q) => [q.uid, q])),
    [startTeaser]
  );

  return (
    <SurveyStartTeaser {...props}>
      <div ref={spyRef} />

      {data ? (
        data?.surveyResults.questions.map((q) => (
          <SurveyStartTeaserSlide
            key={q.uid}
            headline={formatSlideHeadline(questionConfigs[q.uid].question)}
          >
            <SurveyResult questionResults={q} />
          </SurveyStartTeaserSlide>
        ))
      ) : (
        <SurveyStartSlidePlaceholder />
      )}
    </SurveyStartTeaser>
  );
};

export default StartTeaser;
